import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAnimal, useAuth, useShelterPolicy, updateAnimal, ADOPTER_PROFILE_FIELDS } from '@rehome/shared';
import { ArrowLeft, FileCheck, Loader2, Save } from 'lucide-react';

// ─── AnimalRequirementsPage ─────────────────────────────────────────────────
// Own page (was an inline collapsible panel on AnimalProfilePage — moved out
// to declutter the profile). /shelter/:shelterId/animals/:animalId/requirements
// ===========================================================================

/**
 * `embedded`: az állat adatlapjának egyik füleként jelenik meg, nem önálló
 * képernyőként. Ilyenkor elmarad az oldal-keret (vissza-gomb, cím) — azt a
 * befoglaló adatlap adja.
 */
const AnimalRequirementsPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { shelterId, animalId } = useParams<{ shelterId: string; animalId: string }>();
  const navigate = useNavigate();
  const { animal, loading, refetch } = useAnimal(animalId);
  const { userProfile } = useAuth();
  const { data: shelterPolicy } = useShelterPolicy(animal?.shelterId);

  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [savingRequirements, setSavingRequirements] = useState(false);

  useEffect(() => {
    if (animal) {
      const animalFields = animal.requiredAdopterFields ?? [];
      if (animalFields.length > 0) {
        setRequiredFields(animalFields);
      } else if (shelterPolicy?.defaultRequiredFields?.length) {
        setRequiredFields(shelterPolicy.defaultRequiredFields);
      } else {
        setRequiredFields([]);
      }
    }
  }, [animal, shelterPolicy]);

  const canEdit = userProfile?.role === 'shelter_admin' || userProfile?.role === 'super_admin';

  const handleToggleRequirement = (fieldKey: string) => {
    setRequiredFields(prev =>
      prev.includes(fieldKey) ? prev.filter(f => f !== fieldKey) : [...prev, fieldKey]
    );
  };

  const handleSaveRequirements = async () => {
    if (!animalId) return;
    setSavingRequirements(true);
    await updateAnimal(animalId, { requiredAdopterFields: requiredFields });
    setSavingRequirements(false);
    refetch();
  };

  const fieldsByCategory: Record<string, Array<typeof ADOPTER_PROFILE_FIELDS[number]>> = {};
  ADOPTER_PROFILE_FIELDS.forEach(f => {
    if (!fieldsByCategory[f.category]) fieldsByCategory[f.category] = [];
    fieldsByCategory[f.category].push(f);
  });

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="animate-pulse h-40 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  if (!animal) return <div className="p-8 text-center text-gray-500">Állat nem található.</div>;

  return (
    <div className={embedded ? 'space-y-4' : 'max-w-3xl mx-auto space-y-6 pb-12'}>
{!embedded && (<>
      <button onClick={() => navigate(`/shelter/${shelterId}/animals/${animalId}`)} className="flex items-center gap-2 text-gray-500 hover:text-teal-700 transition-colors">
        <ArrowLeft className="w-4 h-4" />Vissza {animal.name} profiljához
      </button>

      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileCheck className="w-6 h-6 text-teal-600" />
          Örökbefogadói igények — {animal.name}
        </h1>
        <p className="text-gray-600 mt-1">Mely adatokat kéred kötelezően az örökbefogadóktól</p>
      </div>
      </>)}

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
        <p className="text-sm text-gray-500">
          Válaszd ki, mely adatokat kéred kötelezően az örökbefogadóktól. A menhely alapértelmezett beállításai
          {shelterPolicy?.defaultRequiredFields?.length ? ' előre ki vannak választva.' : ' alapján még nincs megadva követelmény.'}
          Az állatnál egyedileg felülbírálhatod.
        </p>

        {Object.entries(fieldsByCategory).map(([category, fields]) => (
          <div key={category}>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">{category}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {fields.map(field => (
                <label
                  key={field.key}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                    requiredFields.includes(field.key)
                      ? 'border-teal-300 bg-teal-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  } ${!canEdit ? 'pointer-events-none opacity-70' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={requiredFields.includes(field.key)}
                    onChange={() => handleToggleRequirement(field.key)}
                    disabled={!canEdit}
                    className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{field.label}</p>
                    {field.sensitive && (
                      <span className="text-[10px] text-amber-600 font-medium">Szenzitív adat</span>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        ))}

        {canEdit && (
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <button
              onClick={() => setRequiredFields(shelterPolicy?.defaultRequiredFields ?? [])}
              className="text-xs text-gray-500 hover:text-teal-700 underline"
            >
              Visszaállítás menhelyi alapértelmezettre
            </button>
            <button
              onClick={handleSaveRequirements}
              disabled={savingRequirements}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              {savingRequirements ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Követelmények mentése
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnimalRequirementsPage;
